import { Component, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FileScanDto, FileScanFileDto, FileScanService } from './file-scan.service';
import { DatePipe } from '@angular/common';
import { FileScanItemComponent } from './item/file-scan-item.component';

export type SortColumn = 'path' | 'size' | 'modifiedAt';
export type SortDirection = 'asc' | 'desc';

export interface SortConfig {
  column: SortColumn;
  direction: SortDirection;
}

@Component({
  selector: 'app-file-scan',
  templateUrl: './file-scan.component.html',
  styleUrls: ['./file-scan.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [DatePipe, FileScanItemComponent]
})
export class FileScanComponent implements OnInit {

  scans: FileScanDto[] = [];
  selectedScanUid: string | null = null;
  rootFiles: FileScanFileDto[] = [];
  loadingRoot = false;

  sortConfig: SortConfig = {
    column: 'path',
    direction: 'asc'
  };

  constructor(private fileScanService: FileScanService) {}

  ngOnInit(): void {
    this.fileScanService.getScans().subscribe(scans => {
      this.scans = scans;
    });
  }

  selectScan(scanUid: string): void {
    if (this.selectedScanUid === scanUid) {
      this.selectedScanUid = null;
      this.rootFiles = [];
    } else {
      this.selectedScanUid = scanUid;
      this.loadRootFiles(scanUid);
    }
  }

  loadRootFiles(scanUid: string): void {
    this.loadingRoot = true;
    this.fileScanService.getScanFiles(scanUid).subscribe({
      next: (files) => {
        this.rootFiles = this.sortFiles(files);
        this.loadingRoot = false;
      },
      error: () => {
        this.loadingRoot = false;
      }
    });
  }

  toggleSort(column: SortColumn): void {
    if (this.sortConfig.column === column) {
      this.sortConfig = {
        ...this.sortConfig,
        direction: this.sortConfig.direction === 'asc' ? 'desc' : 'asc'
      };
    } else {
      this.sortConfig = {
        column,
        direction: 'asc'
      };
    }
    this.rootFiles = this.sortFiles(this.rootFiles);
  }

  sortFiles(files: FileScanFileDto[]): FileScanFileDto[] {
    return [...files].sort((a, b) => {
      // Directories always come first
      if (a.type === 'DIRECTORY' && b.type !== 'DIRECTORY') {
        return -1;
      }
      if (a.type !== 'DIRECTORY' && b.type === 'DIRECTORY') {
        return 1;
      }

      const column = this.sortConfig.column;
      const direction = this.sortConfig.direction === 'asc' ? 1 : -1;

      let comparison = 0;
      if (column === 'path') {
        comparison = a.path.localeCompare(b.path);
      } else if (column === 'size') {
        comparison = (a.size || 0) - (b.size || 0);
      } else if (column === 'modifiedAt') {
        const dateA = a.modifiedAt ? new Date(a.modifiedAt).getTime() : 0;
        const dateB = b.modifiedAt ? new Date(b.modifiedAt).getTime() : 0;
        comparison = dateA - dateB;
      }

      return comparison * direction;
    });
  }
}
